import { FC, useMemo, useCallback } from 'react';
import { OrganizationProvider, PlanPage } from '@hologram-hyper-dashboard/components';
import useAppSelector from 'common-js/hooks/useAppSelector';
import { getUserContextDataMemoized } from 'common-js/api/util';
import PlanMenu from 'billing/components/PlanMenu';
import { selectAllPlans } from 'common-js/reducers/activation/selectors';
import { FullHeightCalculatorHOC } from 'common-js/components';
import { browserHistory } from 'react-router';
import {
  AUTH_PARAM_NAME,
  BILLING_DATA_PLAN,
  BILLING_DATA_PLAN_COVERAGE,
  DATA_PLAN_ID_PARAM_NAME,
  DATA_PLAN_ZONE_PARAM_NAME,
  pathMatchesWithIdsReplaced,
  SIM_INVENTORY,
} from 'common-js/constants/paths';

interface PlanProps {
  params: {
    dataPlanId: string;
    dataPlanZone: string;
  };
  location: { pathname: string };
}

type PlanView = GetReactParameterBag<typeof PlanPage>['viewKey'];

const Plan: FC<PlanProps> = ({ params, location }) => {
  const plans: ReturnType<typeof selectAllPlans> = useAppSelector(selectAllPlans);
  const userContext = useAppSelector(getUserContextDataMemoized);
  const orgId = userContext.isInOrgContext ? userContext.orgId : userContext.userOrgId;
  const plan = plans.find((p) => p.id.toString() === params.dataPlanId);

  const path = location.pathname;
  const view: PlanView = useMemo(() => {
    if (pathMatchesWithIdsReplaced(BILLING_DATA_PLAN_COVERAGE, path)) {
      return 'coverage';
    }

    return 'pricing';
  }, [path]);

  const handleTabClick = useCallback(
    (selectedView: PlanView) => {
      const url =
        selectedView === 'pricing' ? BILLING_DATA_PLAN : `${BILLING_DATA_PLAN}/${selectedView}`;
      browserHistory.push({
        pathname: url
          .replace(`:${DATA_PLAN_ID_PARAM_NAME}`, params.dataPlanId)
          .replace(`:${DATA_PLAN_ZONE_PARAM_NAME}`, params.dataPlanZone)
          .replace(`:${AUTH_PARAM_NAME}`, orgId),
      });
    },
    [orgId, params]
  );

  return (
    <OrganizationProvider organizationId={orgId}>
      <PlanMenu plan={plan} />
      <FullHeightCalculatorHOC accountForDrawer backgroundColor="#f8f8fa">
        <PlanPage
          planId={parseInt(params.dataPlanId, 10)}
          zone={params.dataPlanZone}
          viewKey={view}
          handleTabClick={handleTabClick}
          simInventoryBaseUrl={SIM_INVENTORY}
          planCoverageUrl={BILLING_DATA_PLAN_COVERAGE}
        />
      </FullHeightCalculatorHOC>
    </OrganizationProvider>
  );
};

export default Plan;
